import { Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { HubSubNavBar } from "components/hubs/HubSubNavBar";
import { GROUNDLIGHT_SUPPORT_EMAIL } from "config/constants";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { DeviceApp } from "models/hub";
import { Typography } from "new-components/typography/Typography";
import { getHub, getHubAuthCode } from "store/services/Hubs/hubsThunks";
import { handleResetHubs, setCurrentHub } from "store/slices/Hubs/hubsSlice";

function SnackbarContent() {
  return (
    <div>
      <p>
        Oh no! We&apos;ve lost signal. Check that your Vision Core is powered on and connected to the internet. If this
        continues, reach out to groundlight support at{" "}
        <a href={`mailto:${GROUNDLIGHT_SUPPORT_EMAIL}`} style={{ color: "blue", textDecoration: "underline" }}>
          {GROUNDLIGHT_SUPPORT_EMAIL}
        </a>
      </p>
    </div>
  );
}

function HubEmbedError() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={0.5} height="100%">
      <Box>
        <Typography variant="title" fontWeight="bold" className="text-gray-500">
          Sorry,
        </Typography>
      </Box>
      <Box>
        <Typography variant="subtitle" fontWeight="bold" className="text-gray-700">
          We couldn&apos;t connect to the Vision Core.
        </Typography>
      </Box>
      <Box width="40%">
        <Typography align="center">
          Check that your Vision Core is powered on and connected to the internet. If the problem persists, please
          contact support.
        </Typography>
      </Box>
    </Box>
  );
}

export function HubsDetailPageRoute() {
  const { id } = useParams<{ id?: string }>();
  return id ? <HubsDetailPage hubId={id} /> : <div>Please provide an id in the URL!</div>;
}

interface HubAppsProps {
  hubId: string;
}

const HUB_STATUS_APP: DeviceApp = {
  app_description: "Balena device current status",
  app_name: "Details",
  app_num: "",
  app_url: "",
};

export function HubsDetailPage({ hubId }: HubAppsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { currentHub, hubLoading, error } = useAppSelector((state) => state.hubs);
  const { state } = useLocation();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeError, setIframeError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentApp, setCurrentApp] = useState<DeviceApp | null>(null);
  const [authCode, setAuthCode] = useState<string | null>(null);

  const dispatch = useAppDispatch();

  const initialRender = useCallback(async () => {
    // Fetch an SSO auth code up front so the iframe's first request
    // authenticates against the GEP device before loading the app.
    try {
      const result = await dispatch(getHubAuthCode({ hubId }));
      if (result.payload) setAuthCode(result.payload as string);
    } catch {
      // SSO not available — iframe falls back to the direct app URL
    }

    if (state?.selectedHub) dispatch(setCurrentHub(state.selectedHub));
    else {
      const hub = await dispatch(getHub({ hubId }));
      dispatch(setCurrentHub(hub.payload));
    }
  }, [state?.selectedHub, hubId, dispatch]);

  useEffect(() => {
    initialRender();
  }, [initialRender]);

  useEffect(() => {
    if (currentHub && currentHub.status_detail?.all_online && error) dispatch(getHub({ hubId }));
  }, [currentHub, error, hubId, dispatch]);

  const handleLoad = useCallback(() => {
    setLoading(false);
    // The auth code is single-use: after the first iframe load it's consumed
    // server-side and the GEP device has set its session cookie. Clear it so
    // subsequent app switches use the direct URL (session cookie authenticates).
    if (authCode) setAuthCode(null);
  }, [authCode]);

  const handleError = useCallback(() => {
    setIframeError(true);
    handleLoad();
  }, [handleLoad]);

  const title = `Vision Core App ${hubId}`;

  const getBalenaAppUrl = useCallback(() => {
    if (!currentHub?.device_url_base) return "";
    const baseUrl = currentHub.device_url_base;
    // If we have an SSO auth code, route the iframe through the GEP
    // callback to establish a first-party session on the device. The
    // callback redirects to "/" on success; subsequent app navigation
    // within the iframe uses the session cookie directly.
    if (authCode) {
      return `${baseUrl}/hub/auth/janzu/callback?code=${authCode}`;
    }
    // The Details view — and any hub with no configured app — shows GEP's
    // hub status page (hubaxle). Other apps live under their own app_url.
    const path = currentApp?.app_name === HUB_STATUS_APP.app_name ? "/hub/" : currentApp?.app_url ?? "/hub/";
    return `${baseUrl}${path}`;
  }, [currentHub, currentApp, authCode]);

  const balenaAppUrl = getBalenaAppUrl();
  const iframeLoading = hubLoading || loading;

  const handleOpenFrameDialog = (event: MessageEvent) => {
    if (event.data && event.data.type === "open-dialog") {
      setDialogOpen(true);
    } else if (event.data && event.data.type === "close-dialog") {
      setDialogOpen(false);
    }
  };

  useEffect(() => {
    if (state?.hubName && iframeRef.current && !iframeLoading) {
      iframeRef.current?.contentWindow?.postMessage({ type: "hub-name", value: state?.hubName }, "*");
    }
  }, [state?.hubName, iframeRef, iframeLoading]);

  useEffect(() => {
    window.addEventListener("message", handleOpenFrameDialog);
    return () => {
      window.removeEventListener("message", handleOpenFrameDialog);
    };
  }, []);

  useEffect(() => {
    if (hubLoading || currentHub === null) return;
    if (!currentHub) {
      setIframeError(true);
      handleLoad();
      return;
    }

    const status = currentHub.status_detail;

    // The hubaxle status page lives on the device itself, so it loads
    // whenever the hub is reachable — even if the main app (or every app)
    // is offline, or no app is configured at all. Only show the connection
    // error when the hub is genuinely unreachable; otherwise let the iframe
    // load the status page or the running app.
    const hubReachable = status?.endpoint_reachable || status?.hub_online || status?.is_online || false;
    if (!hubReachable) {
      setIframeError(true);
      handleLoad();
      return;
    }

    // Hub is reachable. If UI apps are configured, surface the error only when
    // every configured app is down. With no configured apps, fall through to
    // the hub status page even if stale app_states are present.
    const configuredApps = currentHub.device_apps ?? [];
    const appStates = status?.app_states ?? [];
    if (configuredApps.length === 0 || appStates.length === 0) {
      setIframeError(false);
      return;
    }

    setIframeError(
      configuredApps.every((app) =>
        appStates.some((state) => state.app_name === app.app_name && !state.status)
      )
    );
  }, [currentHub, hubLoading, handleLoad]);

  useEffect(() => {
    if (iframeError) {
      enqueueSnackbar(<SnackbarContent />, { variant: "error" });
    }
  }, [iframeError, enqueueSnackbar]);

  useEffect(() => {
    if (!currentHub) return;

    if (state?.appName === HUB_STATUS_APP.app_name || !currentHub.device_apps?.length) {
      setCurrentApp(HUB_STATUS_APP);
      return;
    }

    // Set the first app that is online as the current app
    const currentApp = currentHub.device_apps.find((app) =>
      currentHub.status_detail?.app_states?.some((state) => state.app_name === app.app_name && state.status)
    );
    setCurrentApp(currentApp || null);
  }, [currentHub, state?.appName]);

  useEffect(
    () => () => {
      dispatch(handleResetHubs());
    },
    [dispatch]
  );

  const handleChangeCurrentApp = (newApp: DeviceApp) => {
    setLoading(true);
    setCurrentApp(newApp);
  };

  return (
    <>
      <HubSubNavBar
        currentHub={currentHub}
        currentApp={currentApp}
        appLoading={iframeLoading}
        onChangeCurrentApp={handleChangeCurrentApp}
      />
      <Box sx={{ height: "calc(100vh - 150px)", overflow: "auto" }}>
        <Box
          position={dialogOpen ? "fixed" : "relative"}
          width="100%"
          height={dialogOpen ? "100vh" : "100%"}
          zIndex={dialogOpen ? 1999 : 1}
          sx={{
            transition: "width 0.3s ease, height 0.3s ease, z-index 0.3s ease, position 0.3s ease",
            top: 0,
            left: 0,
          }}
        >
          {iframeLoading && !iframeError && (
            <Box position="absolute" top="50%" left="50%">
              <CircularProgress size={40} />
            </Box>
          )}
          {error && (
            <Box position="absolute" top="50%" left="50%">
              <p>Error loading Vision Core data: {error}</p>
            </Box>
          )}
          {!!iframeError && <HubEmbedError />}
          {!iframeError && !hubLoading && currentHub?.device_url_base && (
            <iframe
              id="gep-app-frame"
              ref={iframeRef}
              src={balenaAppUrl}
              title={title}
              width="100%"
              height="100%"
              onLoad={handleLoad}
              onError={handleError}
              style={{
                display: iframeLoading ? "none" : "block",
                transition: "width 0.3s ease, height 0.3s ease",
              }}
            />
          )}
        </Box>
      </Box>
    </>
  );
}
